import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Stack,
  Typography,
} from '@mui/material';
import {
  getTenantModuleSubscriptions,
  getTenantSmsUsage,
  getTenantUserSeats,
  recalculateTenantSmsUsage,
} from '../api/pricingApi';
import { getTenantBillingOverview, getTenantPaidPeriods } from '../api/billingPortalApi';
import { getTenantModuleAccessEvents } from '../api/subscriptionChangesApi';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { colorTokens } from '../app/designTokens';
import { DataTable } from './DataTable';
import { DetailField } from './InfoCard';
import { LoadingSkeleton } from './LoadingSkeleton';
import { SectionCard } from './SectionCard';
import { StatusBadge } from './StatusBadge';
import { TenantModuleAccessSection } from './TenantModuleAccessSection';
import { Button } from './ui/Button';
import {
  formatBillingInterval,
  formatBillingStatus,
  formatModuleCode,
  safeTranslate,
} from '../utils/systemLabels';

const { semantic } = colorTokens;
const YEARLY_CHARGED_MONTHS = 11;

function formatPeriod(start, end, t) {
  if (!start && !end) {
    return t('common:empty_value');
  }
  return `${start || '?'} – ${end || '?'}`;
}

function money(t, amount, currency = 'EUR') {
  const value = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  return t('pricing:currency_amount', {
    amount: value.toFixed(2),
    currency: currency || 'EUR',
  });
}

function buildSubscriptionSummary(moduleSubscriptions) {
  const rows = Array.isArray(moduleSubscriptions) ? moduleSubscriptions : [];
  const first = rows[0] || null;
  const multiplier = Number(first?.user_tier_multiplier_snapshot ?? 1) || 1;
  const interval = String(first?.billing_interval_snapshot || 'monthly').toLowerCase();
  const monthlyBaseTotal = rows.reduce(
    (sum, row) => sum + (Number(row.monthly_price_snapshot) || 0),
    0,
  );
  const annualModuleTotal = monthlyBaseTotal * multiplier * YEARLY_CHARGED_MONTHS;

  return {
    user_count: first?.users_count_snapshot ?? null,
    user_tier: first?.user_tier_label_snapshot || null,
    user_multiplier: multiplier,
    billing_interval: interval,
    currency: first?.currency || 'EUR',
    licensed_max_users: first?.licensed_max_users ?? null,
    monthly_module_base_total: monthlyBaseTotal,
    annual_module_total: annualModuleTotal,
    has_modules: rows.length > 0,
  };
}

export function TenantBillingTab({ tenantId }) {
  const { t } = useTranslation(['pricing', 'billing', 'common', 'subscriptionChanges', 'moduleAccessEvents']);
  const { session } = useAuth();
  const canManageSms = hasPermission(session, 'system.billing.sms_usage.manage');

  const [moduleSubscriptions, setModuleSubscriptions] = useState([]);
  const [smsUsage, setSmsUsage] = useState([]);
  const [billingOverview, setBillingOverview] = useState(null);
  const [paidPeriods, setPaidPeriods] = useState([]);
  const [moduleAccessEvents, setModuleAccessEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [recalculating, setRecalculating] = useState(false);
  const [seatUsage, setSeatUsage] = useState(null);

  const load = useCallback(async () => {
    if (!tenantId) return;

    setLoading(true);
    setError('');

    try {
      const [subscriptionsResponse, smsResponse, overviewResponse, periodsResponse, eventsResponse, seatsResponse] = await Promise.all([
        getTenantModuleSubscriptions(tenantId),
        getTenantSmsUsage(tenantId, { per_page: 50 }),
        getTenantBillingOverview(tenantId).catch(() => null),
        getTenantPaidPeriods(tenantId, { per_page: 20 }).catch(() => ({ data: [] })),
        getTenantModuleAccessEvents(tenantId).catch(() => []),
        getTenantUserSeats(tenantId).catch(() => null),
      ]);
      setModuleSubscriptions(Array.isArray(subscriptionsResponse) ? subscriptionsResponse : subscriptionsResponse?.data || []);
      setSeatUsage(seatsResponse);
      setSmsUsage(smsResponse.data || []);
      setBillingOverview(overviewResponse);
      setPaidPeriods(periodsResponse.data || []);
      setModuleAccessEvents(Array.isArray(eventsResponse) ? eventsResponse : eventsResponse?.data || []);
    } catch {
      setError(t('pricing:tenant.load_failed'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const subscriptionSummary = useMemo(
    () => buildSubscriptionSummary(moduleSubscriptions),
    [moduleSubscriptions],
  );

  const smsBillingActive = useMemo(
    () => smsUsage.some((row) => (Number(row.billable_sms_count) || 0) > 0 || (Number(row.amount) || 0) > 0),
    [smsUsage],
  );

  const handleRecalculate = async () => {
    setRecalculating(true);
    setMessage('');
    try {
      await recalculateTenantSmsUsage(tenantId);
      setMessage(t('pricing:sms_usage.recalculate_success'));
      load();
    } catch {
      setMessage(t('pricing:sms_usage.recalculate_failed'));
    } finally {
      setRecalculating(false);
    }
  };

  const eventColumns = useMemo(() => [
    {
      id: 'event_type',
      label: t('moduleAccessEvents:eventType'),
      render: (event) => safeTranslate(t, `moduleAccessEvents:${event.event_type}`, event.event_type),
    },
    {
      id: 'module_code',
      label: t('moduleAccessEvents:moduleCode'),
      render: (event) => formatModuleCode(event.module_code) || event.module_code || t('common:empty_value'),
    },
    {
      id: 'effective_at',
      label: t('moduleAccessEvents:effectiveAt'),
      render: (event) => (event.effective_at ? new Date(event.effective_at).toLocaleString() : t('common:empty_value')),
    },
    {
      id: 'created_at',
      label: t('moduleAccessEvents:createdAt'),
      render: (event) => (event.created_at ? new Date(event.created_at).toLocaleString() : t('common:empty_value')),
    },
  ], [t]);

  const paidPeriodColumns = useMemo(() => [
    {
      id: 'period_start',
      label: t('billing:paid_from'),
      render: (period) => period.period_start || t('common:empty_value'),
    },
    {
      id: 'period_end',
      label: t('billing:paid_until'),
      render: (period) => period.period_end || t('common:empty_value'),
    },
    {
      id: 'payment_method',
      label: t('billing:payment_method'),
      render: (period) => period.payment_method || t('common:empty_value'),
    },
    {
      id: 'status',
      label: t('common:status'),
      render: (period) => <StatusBadge status={period.status} category="invoice" size="Sm" />,
    },
  ], [t]);

  const moduleColumns = useMemo(() => {
    const multiplier = subscriptionSummary.user_multiplier || 1;
    const isYearly = subscriptionSummary.billing_interval === 'yearly';
    return [
      {
        id: 'module_code',
        label: t('pricing:tenant.columns.module_code'),
        render: (item) => formatModuleCode(item.module_code) || item.module_code,
      },
      {
        id: 'monthly_base',
        label: t('pricing:tenant.moduleMonthlyBase'),
        render: (item) => money(t, Number(item.monthly_price_snapshot) || 0, item.currency),
      },
      {
        id: 'billed',
        label: isYearly
          ? t('pricing:tenant.moduleAnnualBase')
          : t('pricing:tenant.moduleBilledMonthly'),
        render: (item) => {
          const monthlyBase = Number(item.monthly_price_snapshot) || 0;
          const rowBilled = isYearly
            ? monthlyBase * multiplier * YEARLY_CHARGED_MONTHS
            : monthlyBase * multiplier;
          return money(t, rowBilled, item.currency);
        },
      },
      {
        id: 'status',
        label: t('pricing:tenant.columns.status'),
        render: (item) => (
          <StatusBadge
            status={item.enabled ? 'enabled' : 'disabled'}
            category="generic"
            apiLabel={item.enabled
              ? t('pricing:tenant.moduleEnabled')
              : t('pricing:tenant.moduleDisabled')}
            size="Sm"
            tone={item.enabled ? 'Success' : 'Neutral'}
          />
        ),
      },
    ];
  }, [t, subscriptionSummary.user_multiplier, subscriptionSummary.billing_interval]);

  const smsColumns = useMemo(() => [
    {
      id: 'period',
      label: t('pricing:tenant.columns.period'),
      render: (item) => formatPeriod(item.period_start, item.period_end, t),
    },
    {
      id: 'billable_sms',
      label: t('pricing:tenant.columns.billable_sms'),
      render: (item) => item.billable_sms_count ?? t('common:empty_value'),
    },
    {
      id: 'amount',
      label: t('pricing:tenant.columns.amount'),
      render: (item) => t('pricing:currency_amount', { amount: item.amount, currency: item.currency || 'EUR' }),
    },
    {
      id: 'status',
      label: t('pricing:tenant.columns.status'),
      render: (item) => <StatusBadge status={item.status} category="invoice" size="Sm" />,
    },
  ], [t]);

  if (loading) {
    return <LoadingSkeleton type="Detail" />;
  }

  return (
    <Stack spacing={2.5}>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="info">{message}</Alert>}

      <TenantModuleAccessSection tenantId={tenantId} />

      <SectionCard variant="Default" title={t('moduleAccessEvents:title')} noPadding sx={{ mb: 0 }}>
        <DataTable
          type="Compact"
          embedded
          columns={eventColumns}
          rows={moduleAccessEvents.slice(0, 20)}
          getRowKey={(event) => event.id}
          emptyMessage={t('moduleAccessEvents:noEvents')}
        />
      </SectionCard>

      {billingOverview && (
        <SectionCard variant="Default" title={t('billing:payment_status_section')} sx={{ mb: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <DetailField
              label={t('billing:paid_until')}
              value={billingOverview.paid_until || billingOverview.subscription?.paid_until || t('common:empty_value')}
            />
            <DetailField
              label={t('billing:grace_until')}
              value={billingOverview.grace_until || billingOverview.subscription?.grace_until || t('common:empty_value')}
            />
            <DetailField label={t('billing:payment_flow_status')}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
                {billingOverview.payment_flow_status || billingOverview.subscription?.payment_flow_status ? (
                  <StatusBadge
                    status={billingOverview.payment_flow_status || billingOverview.subscription?.payment_flow_status}
                    category="generic"
                    apiLabel={formatBillingStatus(
                      billingOverview.payment_flow_status || billingOverview.subscription?.payment_flow_status,
                    )}
                    size="Sm"
                  />
                ) : (
                  <Typography sx={{ fontSize: 13, color: semantic.text.primary }}>
                    {t('common:empty_value')}
                  </Typography>
                )}
              </Box>
            </DetailField>
            <DetailField
              label={t('billing:payment_method')}
              value={billingOverview.subscription?.payment_method_preference || t('common:empty_value')}
            />
          </Box>
        </SectionCard>
      )}

      <SectionCard variant="Default" title={t('billing:paid_periods_title')} noPadding sx={{ mb: 0 }}>
        <DataTable
          type="Compact"
          embedded
          columns={paidPeriodColumns}
          rows={paidPeriods}
          getRowKey={(period) => period.id}
          emptyMessage={t('billing:no_paid_periods')}
        />
      </SectionCard>

      {subscriptionSummary.has_modules && (
        <SectionCard variant="Default" title={t('pricing:tenant.subscriptionSummary')} sx={{ mb: 0 }}>
          <Typography sx={{ mb: 2, fontSize: 13, color: semantic.text.muted }}>
            {t('pricing:tenant.userTierAppliesGlobally')}
          </Typography>
          {seatUsage?.over_limit && (
            <Alert severity="warning" sx={{ mb: 2 }}>{t('pricing:tenant.seatOverLimit')}</Alert>
          )}
          {seatUsage && seatUsage.seat_count_available === false && seatUsage.enforcement_enabled && (
            <Typography sx={{ mb: 2, fontSize: 13, color: semantic.text.muted }}>
              {t('pricing:tenant.seatCountUnavailable')}
            </Typography>
          )}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2.5,
            }}
          >
            <DetailField
              label={t('pricing:tenant.userCount')}
              value={subscriptionSummary.user_count ?? t('common:empty_value')}
            />
            <DetailField
              label={t('pricing:tenant.licensedUserLimit')}
              value={
                subscriptionSummary.licensed_max_users != null
                  ? (seatUsage?.occupied_login_seats != null
                    ? `${seatUsage.occupied_login_seats} / ${subscriptionSummary.licensed_max_users}`
                    : String(subscriptionSummary.licensed_max_users))
                  : t('common:empty_value')
              }
            />
            <DetailField
              label={t('pricing:tenant.userTier')}
              value={subscriptionSummary.user_tier || t('common:empty_value')}
            />
            <DetailField
              label={t('pricing:tenant.multiplier')}
              value={`${subscriptionSummary.user_multiplier}×`}
            />
            <DetailField
              label={t('pricing:tenant.billingPeriod')}
              value={
                formatBillingInterval(subscriptionSummary.billing_interval)
                || subscriptionSummary.billing_interval
                || t('common:empty_value')
              }
            />
            <DetailField
              label={t('pricing:tenant.monthlyModuleBaseTotal')}
              value={money(t, subscriptionSummary.monthly_module_base_total, subscriptionSummary.currency)}
            />
            <DetailField
              label={t('pricing:tenant.annualModuleTotal')}
              value={money(t, subscriptionSummary.annual_module_total, subscriptionSummary.currency)}
            />
            <DetailField label={t('pricing:tenant.smsNotifications')}>
              <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 28 }}>
                <StatusBadge
                  status={smsBillingActive ? 'active' : 'inactive'}
                  category="generic"
                  apiLabel={smsBillingActive
                    ? t('pricing:tenant.smsUsageBillingActive')
                    : t('pricing:tenant.smsUsageBillingInactive')}
                  size="Sm"
                  tone={smsBillingActive ? 'Success' : 'Neutral'}
                />
              </Box>
            </DetailField>
          </Box>
          <Typography sx={{ mt: 1.5, fontSize: 12, color: semantic.text.muted }}>
            {t('pricing:tenant.smsUsageSeparate')}
          </Typography>
        </SectionCard>
      )}

      <SectionCard variant="Default" title={t('pricing:tenant.module_subscriptions')} noPadding sx={{ mb: 0 }}>
        <DataTable
          type="Compact"
          embedded
          columns={moduleColumns}
          rows={moduleSubscriptions}
          getRowKey={(item) => item.id}
          emptyMessage={t('pricing:tenant.no_module_subscriptions')}
        />
      </SectionCard>

      <SectionCard
        variant="Default"
        title={t('pricing:tenant.sms_usage')}
        actions={canManageSms ? (
          <Button
            variant="Secondary"
            onClick={handleRecalculate}
            loading={recalculating}
            disabled={recalculating}
          >
            {t('pricing:tenant.recalculate_sms')}
          </Button>
        ) : null}
        sx={{ mb: 0 }}
      >
        <DataTable
          type="Compact"
          embedded
          columns={smsColumns}
          rows={smsUsage}
          getRowKey={(item) => item.id}
          emptyMessage={t('pricing:tenant.no_sms_usage')}
        />
      </SectionCard>
    </Stack>
  );
}
